import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, ArrowLeft, Eye, EyeOff, Loader2, Clock3 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  // 1. Hooks & State
  const { login, setTokenAndFetchUser } = useAuth(); // Added helper from context
  const nav = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [providers, setProviders] = useState({ google: false, facebook: false, microsoft: false });
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [oauthEmailToken, setOauthEmailToken] = useState("");
  const [oauthEmail, setOauthEmail] = useState("");
  const [oauthOtp, setOauthOtp] = useState("");
  const [oauthOtpSent, setOauthOtpSent] = useState(false);
  const [oauthEmailBusy, setOauthEmailBusy] = useState(false);


  // Get the redirect path (where the user came from)
  const from = location.state?.from?.pathname || "/app";

  // 2. Handle Token from Social Callback
  const handleOAuthCallback = useCallback(async (token) => {
    setIsProcessingOAuth(true);
    try {
      // ✅ This function should be in your AuthContext 
      // It sets localStorage AND updates the 'user' state
      await setTokenAndFetchUser(token);
      toast.success("Social login successful!");
      nav(from, { replace: true });
    } catch (e) {
      toast.error("Failed to sync user profile");
      setIsProcessingOAuth(false);
    }
  }, [from, nav, setTokenAndFetchUser]);



  useEffect(() => {
  if (params.get("error") === "session_expired" || sessionStorage.getItem("auth_notice") === "session_expired") {
    setSessionExpired(true);
    sessionStorage.removeItem("auth_notice");
  }

  // Fetch providers
  api.get("/auth/providers")
    .then((r) => setProviders(r.data))
    .catch((err) => {
      console.error("API Error:", err.response?.data || err.message);
      toast.error("Server error: Could not load login methods.");
    })
    .finally(() => setLoadingProviders(false));

}, [params]);


// ✅ OAuth success handler
useEffect(() => {
  const token = params.get("token");
  if (token && !isProcessingOAuth) {
    handleOAuthCallback(token);
  }
}, [params, isProcessingOAuth, handleOAuthCallback]);


// ✅ OAuth error handler
useEffect(() => {
  const oauthError = params.get("oauth_error");
  if (oauthError) {
    const msg = oauthError.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    toast.error(`Facebook login failed: ${msg}`);
    setErr("Login failed. Please try again.");
  }
}, [params]);

useEffect(() => {
  const token = params.get("oauth_email_token");
  if (token) {
    setOauthEmailToken(token);
    setOauthOtpSent(false);
    setOauthOtp("");
    setErr("");
  }
}, [params]);

const requestOAuthEmailOtp = async (e) => {
  e.preventDefault();
  setOauthEmailBusy(true);
  try {
    await api.post("/auth/oauth-email/request", {
      token: oauthEmailToken,
      email: oauthEmail,
    });
    setOauthOtpSent(true);
    toast.success("OTP sent to your email.");
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  } finally {
    setOauthEmailBusy(false);
  }
};

const verifyOAuthEmailOtp = async (e) => {
  e.preventDefault();
  setOauthEmailBusy(true);
  try {
    const { data } = await api.post("/auth/oauth-email/verify", {
      token: oauthEmailToken,
      otp: oauthOtp,
    });
    if (data.access_token) {
      await setTokenAndFetchUser(data.access_token);
    }
    toast.success("Email verified. Welcome!");
    nav(from, { replace: true });
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  } finally {
    setOauthEmailBusy(false);
  }
};




  // 3. Email/Password Login
  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    
    if (res.ok) {
      toast.success("Welcome back!");
      nav(from, { replace: true });
    } else {
      setErr(res.error);
    }
  };

  const social = async (provider) => {
    try {
      const { data } = await api.get(`/auth/${provider}/start`);
      // Redirects user to Google/MS/FB login page
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // 4. Full-screen loader if OAuth is processing
  if (isProcessingOAuth) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
        <h2 className="font-heading font-bold text-xl uppercase">Authenticating...</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grid-noise flex items-center justify-center p-4">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 font-bold hover:underline">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>

      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-11 h-11 bg-primary border-2 border-foreground rounded-md shadow-brutal flex items-center justify-center text-white">
              <Sparkles className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-2xl uppercase tracking-tighter">DmFlio</span>
          </div>
          <h1 className="text-3xl font-extrabold">Welcome back.</h1>
          <p className="text-sm text-muted-foreground mt-1 font-medium">Log in to your DmFlio account.</p>
        </div>

        <form onSubmit={onSubmit} className="brutal bg-white p-6 space-y-4">
          {sessionExpired && (
            <div className="relative overflow-hidden border-2 border-foreground rounded-md bg-butter p-3 shadow-brutal-sm animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white border-2 border-foreground rounded-md flex items-center justify-center shrink-0">
                  <Clock3 className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-heading font-extrabold text-sm uppercase">Your session expired</div>
                  <div className="text-xs font-semibold text-muted-foreground">Please log in again to continue.</div>
                </div>
              </div>
            </div>
          )}

          
          {loadingProviders ? (
            <div className="text-center text-xs font-bold uppercase py-2 animate-pulse">Loading auth methods...</div>
          ) : (
            (providers.google || providers.facebook || providers.microsoft) && (
              <div className="space-y-2">
                {providers.google && (
                  <button type="button" onClick={() => social("google")} className="w-full flex items-center justify-center gap-2 bg-white border-2 border-foreground rounded-md py-2.5 font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
                    <GoogleIcon /> Continue with Google
                  </button>
                )}
                {providers.facebook && (
                  <button type="button" onClick={() => social("facebook")} className="w-full flex items-center justify-center gap-2 bg-white border-2 border-foreground rounded-md py-2.5 font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
                    <FacebookIcon /> Continue with Facebook
                  </button>
                )}

                {providers.microsoft && (
                  <button type="button" onClick={() => social("microsoft")} className="w-full flex items-center justify-center gap-2 bg-white border-2 border-foreground rounded-md py-2.5 font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
                    <MicrosoftIcon /> Continue with Microsoft
                  </button>
                )}
                
                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-foreground"></div></div>
                  <div className="relative flex justify-center"><span className="bg-white px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">or with email</span></div>
                </div>
              </div>
            )
          )}

          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="brutal-input w-full" placeholder="you@example.com" />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Password</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  required 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="brutal-input w-full pr-10" 
                  placeholder="••••••••" 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {err && (
            <div className="text-sm font-semibold text-destructive bg-destructive/10 border-2 border-destructive p-3 rounded-md">
              {err}
            </div>
          )}
          
          <button 
            type="submit" 
            disabled={busy} 
            className="w-full bg-primary border-2 border-foreground rounded-md py-3 font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Log in"}
          </button>
          
          <div className="flex items-center justify-between text-sm font-medium pt-2">
            <Link to="/forgot-password" size="sm" className="font-bold underline text-xs uppercase">Forgot password?</Link>
            <Link to="/register" size="sm" className="font-bold underline text-xs uppercase">Sign up</Link>
          </div>
        </form>
      </div>

      {oauthEmailToken && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form
            onSubmit={oauthOtpSent ? verifyOAuthEmailOtp : requestOAuthEmailOtp}
            className="brutal bg-white p-6 w-full max-w-md space-y-4"
          >
            <div>
              <h2 className="font-heading font-extrabold text-2xl">Verify your email</h2>
              <p className="text-sm text-muted-foreground font-medium mt-1">
                Facebook did not provide an email. Add one so OTPs can be sent for account deletion and security checks.
              </p>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Email</label>
              <input
                type="email"
                required
                disabled={oauthOtpSent || oauthEmailBusy}
                value={oauthEmail}
                onChange={(e) => setOauthEmail(e.target.value)}
                className="brutal-input w-full disabled:opacity-70"
                placeholder="you@example.com"
              />
            </div>

            {oauthOtpSent && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">OTP</label>
                <input
                  required
                  inputMode="numeric"
                  value={oauthOtp}
                  onChange={(e) => setOauthOtp(e.target.value)}
                  className="brutal-input w-full"
                  placeholder="6-digit code"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={oauthEmailBusy}
              className="w-full bg-primary border-2 border-foreground rounded-md py-3 font-bold shadow-brutal-sm disabled:opacity-50"
            >
              {oauthEmailBusy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : oauthOtpSent ? "Verify and continue" : "Send OTP"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// Separate Icon Component for cleanliness
const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);

const MicrosoftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24"><rect x="2" y="2" width="4.5" height="4.5" fill="#F25022"/><rect x="7.5" y="2" width="4.5" height="4.5" fill="#7FBA00"/><rect x="2" y="7.5" width="4.5" height="4.5" fill="#00A4EF"/><rect x="7.5" y="7.5" width="4.5" height="4.5" fill="#FFB900"/></svg>
);
